import './ApprovalList.css';

import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { Flex, Gutter, Input } from '@gaiads/telia-react-component-library';
import { CustomPaginator, GenericTable } from '@cdaas/reusable-components-daas';

import { ApprovalStatus, ProductOrder } from '@cdaas/api-clients/dist/order-openapi';
import { ApprovalChainStepApprovalRequestDecisionEnum } from '@cdaas/api-clients/dist/asset-user-openapi';

import { modalClose, modalShow, showGrowlNotification } from '../../store/actions';
import { approveOrRejectOrders, getMyOrderById, getOrderPriceText, devLogger } from '../../utils';
import { DATE_FORMAT, DEFAULT_PAGE_SIZE } from '../../utils/constants';

import OrderDetails from '../../Components/OrderDetails/OrderDetails';
import ApprovalStatusIndicator from '../../Components/ApprovalStatusIndicator/ApprovalStatusIndicator';
import ApproveOrRejectOrder from './ApproveOrRejectOrder';

interface ApprovalOrRejectionFlow {
  finished: boolean;
  orderRows: ProductOrder[] | null;
  mode: ApprovalChainStepApprovalRequestDecisionEnum | null;
}

interface ApprovalListProps {
  approvals: ProductOrder[];
  getOrderApprovals: () => Promise<void>;
}

// This is used in order status filtering that is commented out in MVP
// interface ApprovalFilterOptions {
//   name: ApprovalFilterEnum
//   selected: boolean
// }

// This is used in order status filtering that is commented out in MVP
// const initialFilterOptions = [
//   { name: ApprovalFilterEnum.Approved, selected: false },
//   { name: ApprovalFilterEnum.PendingApproval, selected: false },
//   { name: ApprovalFilterEnum.Rejected, selected: false }
// ]

const ApprovalList: FunctionComponent<ApprovalListProps> = ({ approvals, getOrderApprovals }) => {
  // This is used in order status filtering that is commented out in MVP
  // const [filterOptions, setFilterOptions] = useState<ApprovalFilterOptions[]>(initialFilterOptions)
  const [internalApprovals, setInternalApprovals] = useState<ProductOrder[]>([]);
  const [currentPage, setCurrentPage] = useState<ProductOrder[]>([]);

  const [searchText, setSearchText] = useState<string>('');

  const [commentOnApproveOrRejection, setCommentOnApproveOrRejection] = useState<string>('');
  const [orderApprovalOrRejectionFlow, setOrderApprovalOrRejectionFlow] = useState<ApprovalOrRejectionFlow>({
    finished: false,
    orderRows: null,
    mode: null,
  });

  const dispatch = useDispatch();
  const params = useParams<{ id?: string }>();

  const firstUpdate = useRef(true);

  const { t } = useTranslation('approval');
  const { t: bundleTranslation } = useTranslation('bundle');
  const { t: globalTranslation } = useTranslation('globalComponents');

  useEffect(() => {
    devLogger(['approvals useeffect', approvals]);
    setInternalApprovals(approvals);
    // Make sure that if new empty list is received then clear the page
    if (!approvals.length) {
      setCurrentPage([]);
    }
  }, [approvals]);

  useEffect(() => {
    const isSearchTextMatch = (searchText: string, { given_name, family_name, email }: { given_name: string; family_name: string; email: string }) =>
      !searchText || [given_name, family_name, email].some(name => name.toLowerCase().includes(searchText.toLowerCase()));

    const filterApprovalsBySearchText = (approvals: ProductOrder[], searchText: string): ProductOrder[] =>
      approvals.filter(({ packages }) => {
        const { given_name = '', family_name = '', email = '' } = packages?.[0]?.end_user ?? {};
        return isSearchTextMatch(searchText, { given_name, family_name, email });
      });

    const handleFilterApprovals = (
      approvals: ProductOrder[],
      searchText: string,
      setInternalApprovals: (approvals: ProductOrder[]) => void,
    ): void => {
      devLogger(['search text: ', searchText]);
      devLogger([approvals]);
      const tempApprovals = filterApprovalsBySearchText(approvals, searchText);
      setInternalApprovals(tempApprovals);
    };
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    const timeOutId = setTimeout(() => {
      devLogger(['filtering with!!', searchText]);
      handleFilterApprovals(approvals, searchText, setInternalApprovals);
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [searchText, approvals]);

  useEffect(() => {
    const handleOrderApprovalOrRejection = async (
      orderRows: ProductOrder[],
      comment: string,
      mode: ApprovalChainStepApprovalRequestDecisionEnum,
    ): Promise<void> => {
      devLogger([orderRows, comment, mode]);
      const res = await approveOrRejectOrders(orderRows, comment, mode);
      if (!res) {
        dispatch(showGrowlNotification(t('approveOrRejectOrderError'), 'error'));
      }
      setOrderApprovalOrRejectionFlow({ finished: false, orderRows: null, mode: null });
      getOrderApprovals();
    };

    if (orderApprovalOrRejectionFlow.finished && orderApprovalOrRejectionFlow.orderRows) {
      handleOrderApprovalOrRejection(orderApprovalOrRejectionFlow.orderRows, commentOnApproveOrRejection, orderApprovalOrRejectionFlow.mode!);
    }
  }, [orderApprovalOrRejectionFlow, commentOnApproveOrRejection, getOrderApprovals, dispatch, t]);

  const openApproveOrRejectOrderModal = (
    orderRows: ProductOrder[],
    mode: ApprovalChainStepApprovalRequestDecisionEnum,
    destructive: boolean,
  ): void => {
    dispatch(
      modalShow(
        mode === ApprovalChainStepApprovalRequestDecisionEnum.Approved
          ? `${t('ApproveOrRejectOrder.approveOrder')} (${orderRows.length})`
          : `${t('ApproveOrRejectOrder.rejectOrder')} (${orderRows.length})`,
        mode === ApprovalChainStepApprovalRequestDecisionEnum.Approved ? t('approve') : t('reject'),
        t('ApproveOrRejectOrder.goBack'),
        () => {
          setOrderApprovalOrRejectionFlow({ finished: true, orderRows: orderRows, mode });
          dispatch(modalClose());
        },
        () => dispatch(modalClose()),
        <ApproveOrRejectOrder mode={mode} setCommentOnApproveOrRejection={setCommentOnApproveOrRejection} />,
        'lg',
        destructive,
      ),
    );
  };

  const handleOpenOrderRow = (orderRow: ProductOrder): void => {
    devLogger([orderRow]);
    const productName = orderRow.packages[0].order_lines.find(line => line.is_main_product)?.name ?? 'unknown product';
    dispatch(
      modalShow(
        `${t('OrderDetails.orderDetailsTitle')}: ${productName}`,
        '',
        '',
        () => {},
        () => dispatch(modalClose()),
        <OrderDetails
          order={orderRow}
          showBottomButtons={orderRow.customer_approval.status === 'PENDING'}
          openApproveOrRejectOrderModal={openApproveOrRejectOrderModal}
          modalType="approval"
        />,
        'lg',
        false,
      ),
    );
  };

  useEffect(() => {
    const getOrderById = async (id: string) => {
      const order = await getMyOrderById(id);
      if (order) handleOpenOrderRow(order);
      else dispatch(showGrowlNotification(globalTranslation('getOrderError'), 'warning'));
    };

    if (params.id) {
      getOrderById(params.id);
    }
    // skipping used functions from being dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // This is used in order status filtering that is commented out in MVP
  // const handleOnFilterChipClick = (i: number): void => {
  //   setFilterOptions(prevState => {
  //     const newState = prevState.map((option, index) =>
  //       index === i ? { ...option, selected: !option.selected } : option
  //     )
  //     const filterArray = newState.filter(option => option.selected).map(option => option.name)
  //     setInternalApprovals(
  //       filterArray.length
  //         ? approvals.filter((approval: ProductOrder) =>
  //           filterArray.includes(handleApprovalStatusMappingToFrontendEnum(approval.customer_approval.status))
  //         )
  //         : approvals
  //     )
  //     return newState
  //   })
  // }

  const formatApprovalStatus = (status: ApprovalStatus) => <ApprovalStatusIndicator status={status} />;

  return (
    <>
      <Flex centeredVertically spaceBetween>
        {/* Search input */}
        <Input
          showCustomLabel
          className={'approval-search-input'}
          label={''}
          placeholder={t('ApprovalList.searchPlaceholder')}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          iconName={'search'}
        />
        {/* HIDDEN AT THIS MVP STAGE */}
        {/* Status filterchip */}
        {/* <Div>
          <Flex centeredVertically>
            <BodyText size={'md'} color={'black'} fontWeight={'bold'} margin={{ right: 'xs' }}>
              {t('ApprovalList.filterToStatus')}
            </BodyText>
            {filterOptions.map((option, i) => (
              <FilterChip
                key={`filter-chip-${i}`}
                clickableAppearance
                margin={{ size: 'xs' }}
                className={`filter-chips ${option.selected ? 'filter-chips-selected' : ''}`}
                text={t(`ApprovalList.orderStatusOptions.${option.name}`)}
                onClick={() => {
                  handleOnFilterChipClick(i)
                }}
              ></FilterChip>
            ))}
          </Flex>
        </Div> */}
      </Flex>

      <Gutter />

      <GenericTable
        loading={'none'}
        noDataText={t('ApprovalList.noOrdersToApprove')}
        inputData={currentPage}
        checkboxConfig={{
          selectedRowsText: t('BottomApproveOrRejectOrders.ordersSelected'),
          allowSelectingSingleRow: (row: ProductOrder) => {
            if (row.customer_approval.status !== 'PENDING') {
              dispatch(showGrowlNotification(t('notificationText'), 'warning', 5000));
              return false;
            }
            return true;
          },
          allowSelectingAllRows: (rows: ProductOrder[]) => {
            const selected = rows.map(row => ({ row: row, allowed: row.customer_approval.status === 'PENDING' }));
            const someNotSelected = selected.some(row => !row.allowed);
            if (someNotSelected) {
              dispatch(showGrowlNotification(t('notificationText'), 'warning', 5000));
            }
            return selected;
          },
          firstActionButton: {
            name: t('reject'),
            buttonProps: { variant: 'destructive' },
            buttonClickCallback: (rows: ProductOrder[]) => {
              openApproveOrRejectOrderModal(rows, ApprovalChainStepApprovalRequestDecisionEnum.Rejected, true);
              return Promise.resolve('leaveAsIs');
            },
          },
          secondActionButton: {
            name: t('approve'),
            buttonProps: { variant: 'primaryWhite' },
            buttonClickCallback: (rows: ProductOrder[]) => {
              openApproveOrRejectOrderModal(rows, ApprovalChainStepApprovalRequestDecisionEnum.Approved, true);
              return Promise.resolve('leaveAsIs');
            },
          },
          unselectButton: {
            name: t('BottomApproveOrRejectOrders.unselect'),
          },
        }}
        rowConfigs={[
          {
            header: t('ApprovalList.tableHeaders.User'),
            key: 'user',
            formatValue: (_, row) => {
              const { given_name: givenName = '', family_name: familyName = '', email = '' } = row.packages?.[0]?.end_user ?? {};
              return givenName || familyName ? `${givenName} ${familyName}` : email;
            },
          },
          {
            header: t('ApprovalList.tableHeaders.Item'),
            key: 'item',
            formatValue: (_, row) => `${row.packages[0].order_lines.find(line => line.is_main_product)?.name ?? ''}`,
          },
          {
            header: t('ApprovalList.tableHeaders.Price'),
            key: 'price',
            formatValue: (_, row) =>
              row.packages?.[0]?.prices?.length ? getOrderPriceText(row, bundleTranslation('IndividualProductBundle.month')) : '',
          },
          {
            header: t('ApprovalList.tableHeaders.DateOfOrder'),
            key: 'created',
            formatValue: created => (created ? DateTime.fromISO(created).toFormat(DATE_FORMAT) : ''),
          },
          {
            header: t('ApprovalList.tableHeaders.Status'),
            key: 'customer_approval.status',
            formatValue: formatApprovalStatus,
          },
        ]}
        buttonConfig={{
          name: t('ApprovalList.showButton'),
          buttonClickCallback: (row: ProductOrder) => handleOpenOrderRow(row),
          buttonProps: {
            variant: 'secondary',
            iconName: 'chevron-right',
            iconOnRight: true,
          },
        }}
      />

      {internalApprovals?.length > 0 ? (
        <CustomPaginator
          activateInputData={{
            inputData: internalApprovals,
            pageSize: DEFAULT_PAGE_SIZE,
          }}
          onPageChange={pageChangeInfo => setCurrentPage(pageChangeInfo.nextPageItems)}
          paginatorStyleProps={{
            paginationProps: {},
            pageSelectorProps: { centered: true },
          }}
        />
      ) : null}
    </>
  );
};

export default ApprovalList;
